import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { parseLocalDate } from "../../utils/dateUtils";

export default function PlannerContextMenu({
  x,
  y,
  dateStr,
  dayInfo,
  user,
  isAdmin,
  onAction,
  onClose,
}) {
  const [leaveNote, setLeaveNote] = useState("");
  const [menuPosition, setMenuPosition] = useState({ top: y, left: x });
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  useEffect(() => {
    const defaultNote =
      dayInfo?.type === "OFF" && dayInfo?.overrideNote
        ? String(dayInfo.overrideNote)
        : "Leave";
    setLeaveNote(defaultNote);
  }, [dateStr, dayInfo?.type, dayInfo?.overrideNote]);

  useLayoutEffect(() => {
    const menuEl = menuRef.current;
    if (!menuEl) {
      setMenuPosition({ top: y, left: x });
      return;
    }

    const padding = 8;
    const rect = menuEl.getBoundingClientRect();
    const maxLeft = Math.max(padding, window.innerWidth - rect.width - padding);
    const maxTop = Math.max(
      padding,
      window.innerHeight - rect.height - padding,
    );
    const nextLeft = Math.min(Math.max(x, padding), maxLeft);
    const nextTop = Math.min(Math.max(y, padding), maxTop);

    setMenuPosition((prev) =>
      prev.left === nextLeft && prev.top === nextTop
        ? prev
        : { top: nextTop, left: nextLeft },
    );
  }, [x, y, dayInfo?.type, dayInfo?.overrideNote]);

  const dateObj = parseLocalDate(dateStr) || new Date();
  const formattedDate = dateObj.toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  const isCurrentlyWorking =
    dayInfo?.type === "WORKING" || dayInfo?.type === "HALF_DAY";
  const hasOverride = dayInfo?.source === "USER_OVERRIDE";
  const showAdminActions = isAdmin && user?.id;

  const submitLeave = () => {
    onAction("MARK_LEAVE", {
      dateStr,
      note: String(leaveNote || "").trim() || "Leave",
    });
  };

  return (
    <>
      <div
        className="context-menu-overlay"
        onClick={onClose}
        onContextMenu={(e) => {
          e.preventDefault();
          onClose();
        }}
      />
      <div
        ref={menuRef}
        className="context-menu"
        style={{ top: menuPosition.top, left: menuPosition.left }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="context-menu-header">
          <span className="context-date">{formattedDate}</span>
          {user && (
            <span className="context-user">
              {user.firstName} {user.lastName}
            </span>
          )}
        </div>

        <div className="context-menu-divider" />

        <div className="context-current-status">
          {dayInfo?.type === "WORKING" && "Working day"}
          {dayInfo?.type === "HALF_DAY" && "Half day"}
          {dayInfo?.type === "OFF" && `${dayInfo?.overrideNote || "Leave"}`}
          {dayInfo?.type === "WEEKLY_OFF" && "Weekly off (recurring)"}
          {dayInfo?.type === "HOLIDAY" &&
            `${dayInfo.label || "Public holiday"}`}
        </div>

        <div className="context-menu-divider" />

        {showAdminActions && (
          <>
            {isCurrentlyWorking && (
              <>
                <div className="context-note-block">
                  <label
                    className="context-note-label"
                    htmlFor="leave-note-input"
                  >
                    Leave note
                  </label>
                  <input
                    id="leave-note-input"
                    className="context-note-input"
                    value={leaveNote}
                    onChange={(e) => setLeaveNote(e.target.value)}
                    placeholder="Leave"
                  />
                </div>
                <button className="context-menu-item" onClick={submitLeave}>
                  Mark as Leave
                  <span className="context-item-hint">
                    Off for this day only
                  </span>
                </button>
                <button
                  className="context-menu-item"
                  onClick={() => onAction("MARK_HALF_DAY", { dateStr })}
                >
                  Mark as Half Day
                  <span className="context-item-hint">Half working day</span>
                </button>
                {/* <button
                  className="context-menu-item context-menu-item--danger"
                  onClick={() => onAction("MARK_RECURRING_OFF", { dateStr })}
                >
                  Mark as Weekly Off
                  <span className="context-item-hint">
                    Remove this weekday from schedule
                  </span>
                </button> */}
              </>
            )}

            {dayInfo?.type === "WEEKLY_OFF" && (
              <button
                className="context-menu-item"
                onClick={() => onAction("MARK_ONE_OFF_WORKING", { dateStr })}
              >
                Mark as Working This Day
                <span className="context-item-hint">One-time exception</span>
              </button>
            )}

            {dayInfo?.type === "OFF" && (
              <button
                className="context-menu-item"
                onClick={() => onAction("MARK_HALF_DAY", { dateStr })}
              >
                Change to Half Day
              </button>
            )}

            {dayInfo?.type === "HALF_DAY" && (
              <>
                <button className="context-menu-item" onClick={submitLeave}>
                  Change to Full Leave
                </button>
                <button
                  className="context-menu-item"
                  onClick={() => onAction("RESTORE_DAY", { dateStr })}
                >
                  Restore to Working Day
                </button>
              </>
            )}

            {hasOverride && (
              <>
                <div className="context-menu-divider" />
                <button
                  className="context-menu-item context-menu-item--muted"
                  onClick={() => onAction("REMOVE_OVERRIDE", { dateStr })}
                >
                  Restore to Default
                  <span className="context-item-hint">
                    Remove custom setting for this day
                  </span>
                </button>
              </>
            )}
          </>
        )}

        <div className="context-menu-divider" />
        <button
          className="context-menu-item"
          onClick={() => onAction("VIEW_TASKS", { dateStr })}
        >
          View Tasks This Day
        </button>
      </div>
    </>
  );
}
